import { registerLocaleData } from '@angular/common';
import { SupportedLanguageLocale } from '@move/core';
import { TranslateService } from '@ngx-translate/core';
import * as localeEn from '@angular/common/locales/en';
import * as localeExtraEn from '@angular/common/locales/extra/en';


/**
 * The first entry is the Angular locale ID, the second entry is the Angular extra locale ID.
 * Mapping is only required if the internal ID is different from the Angular ID.
 *
 * Check the following folders for available locale data:
 * - node_modules/@angular/common/locales
 * - node_modules/@angular/common/locales/extra
 */
const LOCALE_MAPPING: Partial<Record<SupportedLanguageLocale, [string, string]>> = {
  'da-DK': ['da', 'da'],
  'de-DE': ['de', 'de'],
  'el-GR': ['el', 'el'],
  'en-US': ['en', 'en'],
  'es-ES': ['es', 'es'],
  'fi-FI': ['fi', 'fi'],
  'fr-FR': ['fr', 'fr'],
  'it-IT': ['it', 'it'],
  'nb-NO': ['nb', 'nb'],
  'nl-NL': ['nl', 'nl'],
  'sv-SE': ['sv', 'sv']
};

const importLocaleData = async (localeId: string): Promise<any> => {
  /* Instruct webpack to lazy-load the locale data files.
   * The node_modules prefix is required, see https://github.com/angular/angular/issues/20487#issuecomment-1022229075 */
  return import(
    /* webpackMode: "lazy" */
    `node_modules/@angular/common/locales/${localeId}.mjs`
    ).catch(() => {
    if (localeId.length > 2) {
      return importLocaleData(localeId.slice(0, 2));
    }

    return localeExtraEn;
  });
};

const importExtraLocaleData = async (localeId: string): Promise<any> => {
  /* Instruct webpack to lazy-load the locale data files.
   * The node_modules prefix is required, see https://github.com/angular/angular/issues/20487#issuecomment-1022229075 */
  return import(
    /* webpackMode: "lazy" */
    `node_modules/@angular/common/locales/extra/${localeId}.mjs`
    ).catch(() => {
    if (localeId.length > 2) {
      return importLocaleData(localeId.slice(0, 2));
    }

    return localeEn;
  });
};

async function loadAndRegisterLocaleData(localeId: string): Promise<void> {
  if (!localeId) {
    return;
  }

  const [angularLocaleId, angularExtraLocaleId] = LOCALE_MAPPING[localeId as SupportedLanguageLocale] ?? [
    localeId,
    localeId
  ];

  console.debug(`[localeInitializer] Loading locale data for ${localeId} (mapped to ${angularLocaleId} and ${angularExtraLocaleId})...`);

  try {
    /* Instruct webpack to lazy-load the locale data files. Ensure that all required files are whitelisted in the
     * webpackInclude magic comment so that webpack will not filter these files in the outcome bundles!
     * The node_modules prefix is required, see https://github.com/angular/angular/issues/20487#issuecomment-1022229075 */
    const localeData = await importLocaleData(angularLocaleId);
    const localeExtraData = await importExtraLocaleData(angularExtraLocaleId);

    registerLocaleData(localeData.default, localeId, localeExtraData.default);
    console.log(`[localeInitializer] Locale data for ${localeId} loaded.`);
  } catch (e) {
    /*
     * If the locale ID is undefined (happens with the first onLangChange event), we prevent the loading of locale
     * data because it would throw an error.
     */
    console.error(`[localeInitializer] Error loading locale data for ${localeId}.`, e);
    return;
  }
}

export function localeInitializer(localeId: string, service: TranslateService) {
  service.onLangChange.subscribe((event) => {
    loadAndRegisterLocaleData(event.lang).then();
  });

  return async (): Promise<void> => loadAndRegisterLocaleData(localeId);
}
